import styled from 'styled-components/macro';

import tooltipImg from '../../assets/images/tooltip.png';
import { Small2 } from '../Text';

type Size = 's' | 'm' | 'l';
type Position = 'top' | 'right' | 'bottom' | 'left';

const Container = styled.div<{ disabled: boolean }>`
  opacity: ${({ disabled }) => (disabled ? '0.2' : '1')};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    > * {
      display: ${({ disabled }) => (disabled ? 'none' : 'block')};
    }
  }
`;

const Icon = styled.img<{ size: Size; disabled: boolean }>`
  filter: ${({ disabled }) => (disabled ? 'grayscale(100%)' : 'none')};
  width: ${({ size }) => {
    if (size === 's') {
      return '0.75rem';
    }
    if (size === 'm') {
      return '1rem';
    }
    return '1.25rem';
  }};
  height: ${({ size }) => {
    if (size === 's') {
      return '0.75rem';
    }
    if (size === 'm') {
      return '1rem';
    }
    return '1.25rem';
  }};
`;

const InfoText = styled(Small2)<{ position: Position; offset: number }>`
  display: none;
  position: absolute;
  z-index: ${({ theme }) => theme.tooltipZ};
  width: 15rem;
  text-wrap: wrap;

  ${({ position, offset }) => {
    if (position === 'top') {
      return `
        top: -0.5rem;
        transform: translate(${offset * 100}%, -100%);
      `;
    }
    if (position === 'right') {
      return `
        right: -0.5rem;
        transform: translate(100%, ${offset * 100}%);
      `;
    }
    if (position === 'bottom') {
      return `
        bottom: -0.5rem;
        transform: translate(${offset * 100}%, 100%);
      `;
    }
    return `
        left: -0.5rem;
        transform: translate(-100%, ${offset * 100}%);
      `;
  }}
  background: ${({ theme }) => theme.bg5};
  color: ${({ theme }) => theme.white};
  border-radius: 0.75rem;
  padding: 0.75rem;

  &:before {
    content: '';
    display: block;
    position: absolute;
    width: 0;
    height: 0;

    ${({ position, offset, theme }) => {
      if (position === 'top') {
        return `
          left: ${50 - offset * 100}%;
          transform: translateX(-50%);
          bottom: -0.375rem;
          border-left: 0.375rem solid transparent;
          border-right: 0.375rem solid transparent;
          border-top: 0.375rem solid ${theme.bg5};
        `;
      }
      if (position === 'right') {
        return `
          top: ${50 - offset * 100}%;
          transform: translateY(-50%);
          left: -0.375rem;
          border-top: 0.375rem solid transparent;
          border-bottom: 0.375rem solid transparent;
          border-right: 0.375rem solid ${theme.bg5};
        `;
      }
      if (position === 'bottom') {
        return `
          left: ${50 - offset * 100}%;
          transform: translateX(-50%);
          top: -0.375rem;
          border-left: 0.375rem solid transparent;
          border-right: 0.375rem solid transparent;
          border-bottom: 0.375rem solid ${theme.bg5};
        `;
      }
      return `
          top: ${50 - offset * 100}%;
          transform: translateY(-50%);
          right: -0.375rem;
          border-top: 0.375rem solid transparent;
          border-bottom: 0.375rem solid transparent;
          border-left: 0.375rem solid ${theme.bg5};
      `;
    }}
  }
`;

interface TooltipProps {
  size: Size;
  position: Position;
  children: React.ReactNode;
  offset?: number;
  disabled?: boolean;
  className?: string;
}

export function Tooltip({
  size,
  position,
  children,
  offset = 0,
  disabled = false,
  className = '',
}: TooltipProps) {
  return (
    <Container disabled={disabled} className={className}>
      <Icon size={size} src={tooltipImg} disabled={disabled} />
      <InfoText as="div" position={position} offset={offset}>
        {children}
      </InfoText>
    </Container>
  );
}

Tooltip.defaultProps = {
  offset: 0,
  disabled: false,
  className: '',
};
